import { Guid } from 'Lib/Guid';
import copy from 'fast-clone';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import webApi from '../../../Lib/WebApi';
import { callApi, camelizeObjectKeys } from '../../Helpers/rxjsHelpers';

type WorksheetGridEndpointConfiguration = {
  [type: string]: {
    /**
     * Endpoint for querying tests
     */
    queryEndpoint: string;
    /**
     * Name of property on response from this endpoint that is an array of tests.
     */
    responseTestsPropertyName: string;

    individualEndpoint: string;
  };
};

const worksheetTypeEndpointConfiguration: WorksheetGridEndpointConfiguration = {
  'Moisture Content Drying Tray': {
    queryEndpoint: 'soil.moistureContentTests.dryingTrays.query',
    responseTestsPropertyName: 'DryingTrays',
    individualEndpoint: 'soil.moistureContentTests.dryingTrays',
  },
  'Moisture Content Dry Mass Reading': {
    queryEndpoint: 'soil.moistureContentTests.dryMassReadings.query',
    responseTestsPropertyName: 'DryMassReadings',
    individualEndpoint: 'soil.moistureContentTests.dryMassReadings',
  },
};

const retrieveEndpointConfigOrThrow = (worksheetType: string) => {
  let config = copy(worksheetTypeEndpointConfiguration[worksheetType]);
  if (!config) {
    throw new Error(`The worksheet type "${worksheetType}" is not supported for grid entry.`);
  }
  if (!webApi[config.queryEndpoint]) {
    throw new Error(`The worksheet type "${worksheetType}" does not have a query endpoint '${config.queryEndpoint}'.`);
  }
  if (!webApi[config.individualEndpoint]) {
    throw new Error(
      `The worksheet type "${worksheetType}" does not have an individual endpoint '${config.individualEndpoint}'.`
    );
  }

  return config;
};

export const callApiGetTests = (worksheetType: string, query: any) => {
  let { queryEndpoint: endpoint, responseTestsPropertyName } = retrieveEndpointConfigOrThrow(worksheetType);

  return callApi(webApi[endpoint].get(query)).pipe(
    map((response) => response.Body[responseTestsPropertyName].map((w: any) => camelizeObjectKeys(w)))
  );
};

export const callApiPutWorksheet = (worksheetType: string, body: any, params: any) => {
  let { individualEndpoint } = retrieveEndpointConfigOrThrow(worksheetType);
  return callApi(webApi[individualEndpoint!].put(body, params)).pipe(map((r) => camelizeObjectKeys(r.Body)));
};

// Always include the header as it currently has status information (isChecked, onSignedReport)
export const callApiGetWorksheet = (worksheetType: string, worksheetId: Guid) => {
  let { individualEndpoint } = retrieveEndpointConfigOrThrow(worksheetType);

  const gets: Array<any> = [];

  // a request made to the root worksheet, should include everything required to render the worksheet
  // so map all props, also make sure they become camel cased.
  gets.push({ target: individualEndpoint, mapResponse: camelizeObjectKeys });

  return forkJoin(
    // Make individual gets
    ...gets.map(({ target }) => callApi(webApi[target].get({ worksheetId }))),
    // Combine result Bodies into a single object.
    (...responses: Array<any>) =>
      responses.reduce(
        (previous, current, index) =>
          gets[index].mapResponse ? { ...previous, ...gets[index].mapResponse(current.Body) } : previous,
        {}
      )
  );
};
